import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, CheckCircle2, ChevronLeft, Coffee, Info, Lock, RefreshCw, Send, X } from 'lucide-react';
import { describePortalError, submitPortalTimesheet } from '../../../api/portal';
import { OPEN_JOB_PUNCH_MESSAGE } from './portalErrors';
import { fmtHours, fmtTime } from './portalTime';
import {
  AnswerState,
  buildSubmitBody,
  buildTimesheetAnswers,
  COMMENT_MAX,
  displayName,
  isRequiredByAnswer,
  PersonName,
  questionText,
  answerString,
  signatureMatches,
  triggeredChildren,
  unansweredQuestionIDs,
  visibleQuestions,
} from './submitModel';
import { renderTypedSignature } from './signatureCanvas';
import { DayTotals, DOW_SHORT, dowIndex, sumTotals, weekLabel } from './timesheetModel';
import {
  ANSWER_COMMENTS,
  ANSWER_CONFIRM_CHECKBOX,
  ANSWER_DATE,
  ANSWER_MEAL_WAIVER,
  ANSWER_YES_NO,
  PortalMealWaiverDates,
  PortalPayPeriod,
  PortalTimesheetQuestion,
  PortalTimesheetQuestionChild,
} from './portalTypes';

/**
 * My Timesheet — weekly submit (handoff §6, screenshots 18–20; D7: 780px wide, body fixed at
 * 470px). Review → questions (/portal/timesheetQuestions, requiredWhen and children as on
 * mobile; the meal-waiver question only when the week has waiver dates) → type to sign (the
 * name on file, case- and space-insensitive; drawn in a cursive font to a PNG) → POST
 * /portal/timesheet with the body mobile sends. 409 OPEN_PUNCH and 400 messages show in the
 * footer; the week locks once it is in.
 */

export interface WizardDay {
  ymd: string;
  punches: string;
  workedMin: number;
  mealQ: boolean;
}

export interface SubmitWizardProps {
  period: PortalPayPeriod;
  days: WizardDay[];
  totals: Map<string, DayTotals>;
  /** null while loading or after a failure (see waiverFailed). */
  waiver: PortalMealWaiverDates | null;
  waiverFailed: boolean;
  questions: PortalTimesheetQuestion[] | null;
  questionsFailed: boolean;
  onRetryLoads: () => void;
  supervisorName: string | null;
  employeeID: number;
  name: PersonName;
  zone: string;
  timeEntryIDs: number[];
  timeEntryHourlyIDs: number[];
  onClose: () => void;
  onSubmitted: () => Promise<void>;
  onSessionExpired: () => void;
  /** The role records claims the portal can't include (portalSubmitBlockedByRole): submit from mobile. */
  roleBlock: string | null;
}

const STEPS = ['Review week', 'Questions', 'Sign & submit'];

function shortDay(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  return `${DOW_SHORT[dowIndex(ymd)]} ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
}

const SubmitWizard: React.FC<SubmitWizardProps> = (props) => {
  const { period, days, totals, waiver, questions, zone, name } = props;
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<AnswerState>({});
  const [sig, setSig] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sigInput = useRef<HTMLInputElement | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 15000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => {
    if (step === 3) window.setTimeout(() => sigInput.current?.focus(), 40);
  }, [step]);

  const waiverDates = useMemo(() => waiver?.waiverDates ?? [], [waiver]);
  const visible = useMemo(() => (questions ? visibleQuestions(questions, waiverDates) : []), [questions, waiverDates]);
  // A configured meal-waiver question can't be shown or hidden correctly without the dates.
  const needsWaiver = !!questions?.some((q) => q.answerType === ANSWER_MEAL_WAIVER);
  const loadProblem =
    props.questionsFailed ? "Couldn't load the timesheet questions."
      : needsWaiver && props.waiverFailed ? "Couldn't check this week's meal periods."
        : null;
  const loadingQs = !questions && !props.questionsFailed;
  const missing = unansweredQuestionIDs(visible, answers);
  const questionsReady = !loadProblem && !loadingQs && !(needsWaiver && !waiver) && missing.length === 0;
  const sigOk = signatureMatches(sig, name);
  const fullName = displayName(name);
  const week = sumTotals(totals, days.map((d) => d.ymd));
  const workedTotal = days.reduce((s, d) => s + d.workedMin, 0);

  const setAnswer = (id: number, value: string | null) => setAnswers((a) => ({ ...a, [id]: value }));

  const submit = async () => {
    if (!sigOk || busy || props.roleBlock) return;
    setBusy(true);
    setError(null);
    let signature: string;
    try {
      signature = await renderTypedSignature(sig);
    } catch {
      setError("Couldn't create your signature in this browser. Try again, or submit from the mobile app.");
      setBusy(false);
      return;
    }
    try {
      const body = buildSubmitBody({
        employeeID: props.employeeID,
        payPeriodID: period.payPeriodID,
        nowMs: Date.now(),
        zone,
        timesheetAnswers: buildTimesheetAnswers(visible, answers, waiverDates),
        signature,
        timeEntryIDs: props.timeEntryIDs,
        timeEntryHourlyIDs: props.timeEntryHourlyIDs,
      });
      await submitPortalTimesheet(body);
      await props.onSubmitted();
    } catch (err) {
      const info = describePortalError(err);
      if (info.kind === 'sessionExpired') {
        props.onSessionExpired();
        setError('Your session expired. Sign in again, then submit.');
      } else if (info.code === 'OPEN_PUNCH' && info.message !== OPEN_JOB_PUNCH_MESSAGE) {
        setError('A punch is still open this week. Clock out first, then submit.');
      } else {
        setError(info.message);
      }
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------ steps */

  const roleNotice = props.roleBlock && (
    <div className="notice warn" role="alert"><Info /><span className="grow">{props.roleBlock}</span></div>
  );

  const review = (
    <>
      {roleNotice}
      <table className="rv">
        <thead>
          <tr>
            <th>Day</th><th>Punches</th><th className="n">Worked</th><th className="n">Reg</th><th className="n">OT</th>
            <th className="n">DT</th><th className="n">Leave</th><th className="n">Total</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const t = totals.get(d.ymd);
            const leave = t ? t.vac + t.sick + t.hol : 0;
            const wk = dowIndex(d.ymd) >= 5;
            const f = (m: number | undefined) => (m ? fmtHours(m) : '—');
            return (
              <tr key={d.ymd} className={wk ? 'wk' : ''}>
                <td>{shortDay(d.ymd)}</td>
                <td className="mono">{d.punches || '—'}{d.mealQ && <> <span className="mflag" title="Meal question at submit"><Coffee /></span></>}</td>
                <td className="n">{f(d.workedMin)}</td>
                <td className="n">{f(t?.reg)}</td>
                <td className="n ot">{f(t?.ot)}</td>
                <td className="n ot">{f(t?.dt)}</td>
                <td className="n">{f(leave)}</td>
                <td className="n b">{f(t?.total)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Week{period.checkYmd ? ' · pays ' + shortDay(period.checkYmd) : ''}</td>
            <td className="n">{fmtHours(workedTotal)}</td>
            <td className="n">{fmtHours(week.reg)}</td>
            <td className="n ot">{fmtHours(week.ot)}</td>
            <td className="n ot">{fmtHours(week.dt)}</td>
            <td className="n">{fmtHours(week.vac + week.sick + week.hol)}</td>
            <td className="n b">{fmtHours(week.total)}</td>
          </tr>
        </tfoot>
      </table>
      <div className="notice holiday">
        <Info />
        <span className="grow">
          Something wrong? Close this and fix the day first — once you submit, punches are locked until your supervisor or payroll unlocks the week.
        </span>
      </div>
    </>
  );

  const yesNo = (id: number, yesLabel: string, value: string | null | undefined) => (
    <div className="qans">
      <button type="button" className={value === 'Yes' ? 'on' : ''} aria-pressed={value === 'Yes'} onClick={() => setAnswer(id, 'Yes')}>{yesLabel}</button>
      <button type="button" className={value === 'No' ? 'on no' : ''} aria-pressed={value === 'No'} onClick={() => setAnswer(id, 'No')}>No</button>
    </div>
  );

  const questionCard = (q: PortalTimesheetQuestion | PortalTimesheetQuestionChild, n: string, child: boolean, required: boolean) => {
    const id = q.timesheetQuestionID;
    const value = answers[id];
    let control: React.ReactNode = null;
    let rule = '';
    let hint: React.ReactNode = '';
    let hintWarn = false;
    switch (q.answerType) {
      case ANSWER_YES_NO:
        control = yesNo(id, 'Yes', value);
        rule = 'Answer Yes or No';
        break;
      case ANSWER_MEAL_WAIVER:
        // Yes only, as on mobile (MealConditionalWaiver validates isEnabled).
        control = (
          <div className="qans">
            <button type="button" className={value === 'Yes' ? 'on' : ''} aria-pressed={value === 'Yes'}
              onClick={() => setAnswer(id, value === 'Yes' ? null : 'Yes')}>
              {answerString(ANSWER_MEAL_WAIVER, 'Yes', waiverDates)}
            </button>
          </div>
        );
        rule = `Dates: ${waiverDates.join(', ')} · required to submit`;
        break;
      case ANSWER_CONFIRM_CHECKBOX:
        control = (
          <div className="qans">
            <button type="button" className={value === 'Yes' ? 'on' : ''} aria-pressed={value === 'Yes'}
              onClick={() => setAnswer(id, value === 'Yes' ? null : 'Yes')}>
              {value === 'Yes' ? <><Check style={{ width: 14, height: 14 }} /> Confirmed</> : 'Confirm'}
            </button>
          </div>
        );
        rule = 'Required to submit';
        break;
      case ANSWER_DATE:
        control = (
          <div className="qans">
            <input className="input mono" type="date" aria-label="Date" value={value ?? ''} onChange={(e) => setAnswer(id, e.target.value || null)} />
          </div>
        );
        rule = 'Pick a date';
        break;
      case ANSWER_COMMENTS:
        control = (
          <textarea className="input" maxLength={COMMENT_MAX} value={value ?? ''} aria-label="Comments"
            onChange={(e) => setAnswer(id, e.target.value.slice(0, COMMENT_MAX))} />
        );
        rule = required ? 'Required because of your answer above' : 'Optional';
        hint = `${(value ?? '').length} / ${COMMENT_MAX}`;
        if (required && !(value ?? '').trim()) hintWarn = true;
        break;
      default:
        return null;
    }
    return (
      <div key={id} className={`qcard${child ? ' child' : ''}`}>
        <div className="qn">{n}</div>
        <div className="qt">{questionText(q.question, waiverDates)}</div>
        <div className="qs">{rule}</div>
        {control}
        <div className={`qhint${hintWarn ? ' warn' : ''}`}>{hint || ' '}</div>
      </div>
    );
  };

  const questionsStep = (() => {
    if (loadProblem) {
      return (
        <div className="notice warn">
          <Info />
          <span className="grow">{loadProblem} Your answers can't be checked without it.</span>
          <button type="button" className="btn sm" onClick={props.onRetryLoads}><RefreshCw />Retry</button>
        </div>
      );
    }
    if (loadingQs || (needsWaiver && !waiver)) return <div className="pk-empty">Loading questions…</div>;
    if (!visible.length) return <div className="pk-empty">No questions this week — go on to sign.</div>;
    const cards: React.ReactNode[] = [];
    visible.forEach((q, k) => {
      const required = q.answerType === ANSWER_COMMENTS && isRequiredByAnswer(q, answers);
      cards.push(questionCard(q, `Question ${k + 1}`, false, required));
      triggeredChildren(q, answers).forEach((c, j) => cards.push(questionCard(c, `Question ${k + 1}.${j + 1}`, true, false)));
    });
    return cards;
  })();

  const summaryRows = buildTimesheetAnswers(visible, answers, waiverDates);
  const allQs = visible.flatMap((q) => [q, ...triggeredChildren(q, answers)]);
  const sign = (
    <>
      {roleNotice}
      <div className="ansum">
        {summaryRows.map((a) => {
          const q = allQs.find((x) => x.timesheetQuestionID === a.timesheetQuestionID);
          return (
            <div key={a.timesheetQuestionID} className="ar">
              <span className="aq">{q ? questionText(q.question, waiverDates) : `Question ${a.timesheetQuestionID}`}</span>
              <span className="aa">{a.answer}</span>
            </div>
          );
        })}
        <div className="ar">
          <span className="aq">
            <b>Hours</b> · {fmtHours(week.reg)} regular · {fmtHours(week.ot + week.dt)} overtime · {fmtHours(week.vac + week.sick + week.hol)} paid leave
          </span>
          <span className="aa">{fmtHours(week.total)} h</span>
        </div>
      </div>
      <div className="sig">
        <label className="flab" htmlFor="etp-sig-in">Type your full name to sign</label>
        <input id="etp-sig-in" ref={sigInput} className="input" autoComplete="off" placeholder={fullName} value={sig}
          disabled={busy} onChange={(e) => { setSig(e.target.value); setError(null); }} />
        <div className={`sig-prev${sigOk ? ' on' : ''}`} aria-hidden="true">{sig.trim() || fullName}</div>
        <div className="sig-meta">
          <span>
            Signed electronically · {new Date(nowMs).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: zone })} · {fmtTime(nowMs, zone)} · Web
          </span>
          <span className={`sig-hint${sigOk ? ' ok' : ''}`}>
            {sigOk ? <><CheckCircle2 />Matches your name on file</> : sig.trim() ? <>Type your name exactly as <b>&nbsp;{fullName}</b></> : ' '}
          </span>
        </div>
      </div>
      <div className="notice holiday">
        <Lock />
        <span className="grow">
          After you submit, this week's punches are locked. {props.supervisorName ?? 'Your supervisor'} approves it next, then payroll.
        </span>
      </div>
    </>
  );

  const stepper = STEPS.map((label, k) => {
    const n = k + 1;
    const cls = n < step ? 'done' : n === step ? 'cur' : '';
    return (
      <React.Fragment key={label}>
        {k > 0 && <span className="stepsep" />}
        <span className={`sf-st ${cls}`}><span className="sn">{n < step ? <Check /> : n}</span>{label}</span>
      </React.Fragment>
    );
  });

  const footNote = error
    ? <span className="grow mfoot-note err" role="alert">{error}</span>
    : step === 2
      ? <span className="grow mfoot-note">{visible.length} question{visible.length === 1 ? '' : 's'} · same as the mobile app</span>
      : <span className="grow" />;

  return (
    <div className="etp-modal-bg" role="presentation">
      <div className="etp-modal sf" role="dialog" aria-modal="true" aria-labelledby="etp-sf-title">
        <div className="modal-head">
          <div className="mi"><Send /></div>
          <div>
            <h3 id="etp-sf-title">Submit {weekLabel(period)}</h3>
            <div className="ms">
              Goes to {props.supervisorName ?? 'payroll'} for approval{period.checkYmd ? ` · pays ${shortDay(period.checkYmd)}` : ''}
            </div>
          </div>
          <button type="button" className="x" aria-label="Close" onClick={props.onClose} disabled={busy}><X /></button>
        </div>
        <div className="sf-steps">{stepper}</div>
        <div className="modal-body sf-body">
          {step === 1 ? review : step === 2 ? questionsStep : sign}
        </div>
        <div className="modal-foot">
          {step === 1 ? (
            <>
              {footNote}
              <button type="button" className="btn" onClick={props.onClose}>Cancel</button>
              <button type="button" className="btn primary" onClick={() => setStep(2)}>Next: questions<ArrowRight /></button>
            </>
          ) : step === 2 ? (
            <>
              <button type="button" className="btn" onClick={() => setStep(1)}><ChevronLeft />Back</button>
              {footNote}
              <button type="button" className="btn primary" disabled={!questionsReady} onClick={() => setStep(3)}>Next: sign<ArrowRight /></button>
            </>
          ) : (
            <>
              <button type="button" className="btn" disabled={busy} onClick={() => { setStep(2); setError(null); }}><ChevronLeft />Back</button>
              {footNote}
              <button type="button" className="btn primary" disabled={!sigOk || busy || !!props.roleBlock} title={props.roleBlock ?? undefined} onClick={() => void submit()}>
                <Send />{busy ? 'Submitting…' : 'Submit week'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmitWizard;
